import { Spacing } from '@persona-id/design-tokens';
import { ComponentPropsWithoutRef } from 'react';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import { SearchListOption } from 'dashboard/forms/FormReportTemplate/SearchListsSelectTable';
import { WatchlistListsDisplay } from 'dashboard/forms/FormReportTemplate/WatchlistListsSelect/WatchlistListsDisplay';
import { WatchlistListsSelectModal } from 'dashboard/forms/FormReportTemplate/WatchlistListsSelect/WatchlistListsSelectModal';
import { translateWatchlistType, WatchlistDefaultTag } from 'dashboard/models/report/watchlist';
import { WatchlistSourceList, WatchlistTag } from 'dashboard/models/report-template';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Label, Span } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';
import useToggle from 'lib/hooks/useToggle';

export interface WatchlistListOption extends SearchListOption {
  flagCode?: string;
  url?: string;
  lists?: string[];
}

const getIntersection = (options: WatchlistListOption[], selected: string[]) => {
  const values = options.map((option) => option.value);
  return values.filter((value) => selected.includes(value));
};

export const mapSourcesToListOptions = (sources: WatchlistSourceList[]): WatchlistListOption[] => {
  return sources.map((source) => {
    return {
      categories: [translateWatchlistType(source.category)],
      name: source.name,
      value: source.slug,
      flagCode: source['flag-code'],
      url: source.source,
    };
  });
};

export const mapSourcesToTagOptions = (tags: WatchlistTag[]): WatchlistListOption[] => {
  return tags.map((tag) => {
    return {
      categories: [tag.category],
      name: tag.name,
      value: tag.slug,
      flagCode: tag['flag-code'],
      lists: tag.lists,
    };
  });
};

interface WatchlistListsSelectProps extends Omit<ComponentPropsWithoutRef<'select'>, 'onChange'> {
  disabled?: boolean;
  error?: string;
  value: string[];
  onChange: (values: string[]) => void;
  sources: WatchlistSourceList[];
  tags: WatchlistTag[];
}

export const WatchlistListsSelect = ({
  value,
  disabled,
  error,
  onChange,
  sources,
  tags,
}: WatchlistListsSelectProps) => {
  const [isModalOpen, { enable: showModal, disable: hideModal }] = useToggle(false);

  const listOptions = sources ? mapSourcesToListOptions(sources) : [];
  const subscriptionOptions = tags ? mapSourcesToTagOptions(tags) : [];
  const subscribedToDefault =
    value.filter(
      (val) => val === WatchlistDefaultTag.Legacy || val === WatchlistDefaultTag.DefaultNoPep,
    ).length > 0;
  const subscriptions = subscribedToDefault
    ? subscriptionOptions.map((option) => option.value)
    : getIntersection(subscriptionOptions, value);
  const lists = getIntersection(listOptions, value);

  return (
    <>
      <Card>
        <StyledBody>
          <EditButton
            id='watchlist-lists-select__edit'
            variant='basic'
            width='auto'
            size='sm'
            type='button'
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              showModal();
            }}
          >
            Edit
          </EditButton>
          <StyledLabel size='lg' color={disabled ? 'disabled' : 'primary'}>
            Sources
          </StyledLabel>
          <Span color={disabled ? 'disabled' : 'primary'}>
            Sources being used to check for matches in this template.
          </Span>
          <Spacer height={1} />
          {subscriptionOptions.length > 0 && listOptions.length > 0 && (
            <WatchlistListsDisplay
              subscribedToDefault={subscribedToDefault}
              subscriptions={subscriptions}
              lists={lists}
              listOptions={listOptions}
              subscriptionOptions={subscriptionOptions}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              disabled={disabled}
            />
          )}
          {!!error && <FormError>{error}</FormError>}
        </StyledBody>
      </Card>
      <ModalWrapper isOpen={isModalOpen}>
        <WatchlistListsSelectModal
          subscribedToDefault={subscribedToDefault}
          subscriptions={subscriptions}
          lists={lists}
          listOptions={listOptions}
          subscriptionOptions={subscriptionOptions}
          handleCancel={hideModal}
          handleSubmit={(values: string[]) => {
            hideModal();
            onChange(values);
          }}
        />
      </ModalWrapper>
    </>
  );
};

const EditButton = styled(Button)`
  position: absolute;
  top: ${Spacing[5]};
  right: ${Spacing[5]};
`;

const StyledLabel = styled(Label)`
  font-weight: ${FontWeight.Bold};
`;

const StyledBody = styled(Card.Body)`
  position: relative;
`;
